import { useEffect, useState } from 'react';
import './notifications.scss';
import { useNotificationsRealtime } from 'context/notifications/NotificationsRealtimeContext';
import { formatDate } from 'utils/formatDate';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { mainLayoutI18n } from '../../layout/MainLayout/i18n';

const getNotificationNavigation = (notification) => {
  if (!notification) return null;

  const { entityType, entityId } = notification;
  if (!entityType || !entityId) return null;

  switch (entityType) {
    case 'Case':
      return {
        pathname: `/detalle-causa/${entityId}/movimientos`,
      };

    case 'Client':
      return {
        pathname: `/crm/detalles-cliente/${entityId}`,
      };

    case 'Task':
      return {
        pathname: '/tareas',
        state: {
          taskIdToOpen: entityId,
        },
      };

    default:
      return null;
  }
};

const Notifications = ({ DesignSystemComponents }) => {
  const { SvgIcon, Button, Skeleton } = DesignSystemComponents;
  const { language } = useLanguage();
  const mainLayoutLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: mainLayoutI18n });

  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    isConnected,
    loadMore,
    hasMore,
    loading,
    markAsRead,
    deleteNotification,
  } = useNotificationsRealtime();

  useEffect(() => {
    mainLayoutI18n.changeLanguage(mainLayoutLanguage);
  }, [mainLayoutLanguage]);

  return (
    <div className='notifications-menu'>
      <button
        type='button'
        className='notifications-menu__trigger d-flex align-items-center gap-2'
        onClick={() => setOpen((prev) => !prev)}
      >
        <SvgIcon
          ariaLabel={t('notifications_open_aria')}
          name='bell'
          size='1rem'
          stroke='var(--dark)'
        />
        <p className='caption'>{t('notifications_title')}</p>
        {notifications.length !== 0 && unreadCount !== 0 && (
          <div className='user-menu__number bg-error-500 d-flex align-items-center justify-content-center'>
            <p className='caption color-white fw-semibold'>{unreadCount}</p>
          </div>
        )}
        {!isConnected && <div className='dot bg-error-500' />}
        <SvgIcon name={open ? 'chevronUp' : 'chevronDown'} size='1rem' />
      </button>

      {open && (
        <div className='notifications-menu__panel'>
          {loading ? (
            <Skeleton height='3rem' width='100%' />
          ) : (
            <>
              {notifications.length !== 0 ? (
                <>
                  <div className='notifications-menu__scroll'>
                    {notifications.map((notification, index) => (
                      <div key={index}>
                        <button
                          type='button'
                          className='notifications-menu__item d-flex justify-content-between'
                          onClick={() => {
                            if (!notification.isRead) {
                              markAsRead(notification.id);
                            }

                            const nav = getNotificationNavigation(notification);
                            if (nav) {
                              navigate(nav.pathname, {
                                state: nav.state ?? null,
                              });
                            }

                            setOpen(false);
                          }}
                        >
                          <div className='d-flex flex-column w-100'>
                            <div className='d-flex justify-content-between'>
                              <p className='caption fw-semibold'>
                                {notification.title}
                              </p>
                              <div className='d-flex'>
                                <SvgIcon
                                  name='trash'
                                  size='1rem'
                                  className='notifications-menu__trash'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification.id);
                                  }}
                                />
                                <div
                                  className={`notifications-menu__circle mt-1 ms-1 ${!notification.isRead ? 'd-block' : 'd-none'}`}
                                />
                              </div>
                            </div>
                            <p className='mini mt-1'>{notification.body}</p>
                            <p className='mini mt-1 color-gray-600'>
                              {formatDate(notification.createdAt, true, true)}
                            </p>
                          </div>
                        </button>
                        {index < notifications.length - 1 && (
                          <div className='notifications-menu__separator' />
                        )}
                      </div>
                    ))}
                  </div>
                  <div
                    className='d-flex justify-content-center align-items-center'
                    style={{
                      height: '3rem',
                      border: '1px solid var(--gray-200)',
                    }}
                  >
                    <Button
                      label={t('notifications_load_more')}
                      onClick={(e) => {
                        e.stopPropagation();
                        loadMore();
                      }}
                      size='sm'
                      variant='naked'
                      disabled={!hasMore}
                    />
                  </div>
                </>
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ height: '3rem' }}
                >
                  <p className='caption fw-semibold'>{t('notifications_empty')}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
